import {
  ConnectedPosition,
  Overlay,
  OverlayPositionBuilder,
  OverlayRef,
  ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  inject,
} from '@angular/core';

import { isEqualWith } from 'lodash';
import { map, take } from 'rxjs';

import { TableTooltipComponent } from '../../components/table-tooltip/table-tooltip.component';
import { TooltipPosition } from '../../models/_table.model';
import { compareConnectedPositions } from '../../utils/table';

@Directive({
  standalone: true,
  selector: '[tsqTooltipTruncated]',
})
export class TooltipTruncatedDirective implements OnDestroy {
  @Input('tsqTooltipTruncated') refEl: HTMLElement;

  private overlayRef: OverlayRef | null = null;

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly positionBuilder = inject(OverlayPositionBuilder);
  private readonly scrollStrategies = inject(ScrollStrategyOptions);
  private readonly overlay = inject(Overlay);

  private readonly positions: Record<TooltipPosition, ConnectedPosition> = {
    top: {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -8,
    },
    left: {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -8,
    },
    right: {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 8,
    },
    bottom: {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 8,
    },
  };

  private readonly positionStrategy = this.positionBuilder
    .flexibleConnectedTo(this.el)
    .withFlexibleDimensions(false)
    .withPush(false)
    .withPositions(Object.values(this.positions));

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  @HostListener('mouseenter')
  private onMouseEnter(): void {
    if (!!this.refEl && this.refEl.offsetWidth < this.refEl.scrollWidth) {
      this.overlayRef ||= this.createOverlay();

      if (!this.overlayRef.hasAttached()) {
        const tooltipPortal = new ComponentPortal(TableTooltipComponent);
        const componentRef = this.overlayRef.attach(tooltipPortal);

        this.setTooltipInnerText(componentRef);
        this.setTooltipPosition(componentRef);
      }
    }
  }

  @HostListener('mouseleave')
  private onMouseLeave(): void {
    this.overlayRef?.detach();
  }

  private createOverlay(): OverlayRef {
    return this.overlay.create({
      positionStrategy: this.positionStrategy,
      scrollStrategy: this.scrollStrategies.close(),
    });
  }

  private setTooltipInnerText(componentRef: ComponentRef<TableTooltipComponent>): void {
    const text = this.refEl.innerText;

    componentRef.instance.setText(text);
  }

  private setTooltipPosition(componentRef: ComponentRef<TableTooltipComponent>): void {
    this.positionStrategy.positionChanges
      .pipe(
        take(1),
        map(({ connectionPair }) => {
          const [tooltipPosition] = Object.entries(this.positions).find(([, connectedPosition]) =>
            isEqualWith(connectedPosition, connectionPair, compareConnectedPositions),
          );
          return (tooltipPosition as TooltipPosition) ?? 'top';
        }),
      )
      .subscribe(tooltipPosition => componentRef.instance.setPosition(tooltipPosition));
  }
}
